import {
  GetProductGroupsQuery,
  GetProductGroupsQueryVariables,
  useGetProductGroupsQuery,
} from "generated/graphql";
import { useCallback, useMemo } from "react";

export const cacheKey = "GetProductGroups";

export default function useGetProductGroups({
  page = 1,
  first = 10,
  search,
}: GetProductGroupsQueryVariables) {
  const {
    data: graphqlData,
    loading,
    error,
    fetchMore: more,
    refetch,
  } = useGetProductGroupsQuery({
    variables: {
      page,
      first,
      search,
    },
  });

  const fetchMore = useCallback(
    async ({ page }: { page: number }) => {
      return more({
        variables: {
          page,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => ({
          ...previousResult,
          getProductGroups: {
            ...previousResult.getProductGroups,
            data: [
              ...previousResult?.getProductGroups?.data,
              ...fetchMoreResult?.getProductGroups?.data,
            ],
          },
        }),
      });
    },
    [more]
  );

  const getData = useCallback(
    ({ data }: { data?: GetProductGroupsQuery }) => {
      return data?.getProductGroups?.data || null;
    },
    [graphqlData]
  );

  const getPaginatorInfo = useCallback(({ data }: { data?: GetProductGroupsQuery }) => {
    return data?.getProductGroups?.paginatorInfo || null;
  }, []);

  const data = useMemo(() => {
    return getData({ data: graphqlData });
  }, [graphqlData, getData]);

  const paginatorInfo = useMemo(() => {
    return getPaginatorInfo({ data: graphqlData });
  }, [graphqlData]);

  const getAllData = useCallback(async () => {
    if (paginatorInfo && paginatorInfo?.total) {
      await refetch({
        first: paginatorInfo?.total ?? first,
        search,
      });
    }
  }, [first, paginatorInfo, refetch, search]);

  return {
    data,
    loading,
    error,
    paginatorInfo,
    fetchMore,
    getData,
    getPaginatorInfo,
    refetch,
    getAllData,
  } as const;
}
